import { computed, inject } from '@angular/core';
import { EAccessCode } from '@app/app.enums';
import { EStateStatus } from '@app/app.state';
import { PvsShiftApiService } from '@app/core/api/pvs-shift-api.service';
import { EPvsShiftStatusCode } from '@app/core/enums/pvs.enum';
import { IHttpError } from '@app/core/interfaces/error.interface';
import { IPvsShiftForemanInfo } from '@app/core/interfaces/pvs-shift.interface';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { parseError } from 'ngx-mt-ui-lib/functions';
import { AccessService } from 'ngx-mt-ui-lib/services/access';
import { ToastService } from 'ngx-mt-ui-lib/services/toast';
import {
  distinctUntilChanged,
  EMPTY,
  exhaustMap,
  Observable,
  pipe,
  switchMap,
  tap,
  timer,
} from 'rxjs';

interface PvsShiftState {
  items: IPvsShiftForemanInfo[];
  status: EStateStatus;
  error: IHttpError | null;
}

const initialState: PvsShiftState = {
  items: [],
  status: EStateStatus.PENDING,
  error: null,
};

export const PVS_SHIFT_REFRESH_INTERVAL_MS = 30_000;

interface PvsShiftLoadOptions {
  readonly silent?: boolean;
}

export const PvsShiftStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ items }) => ({
    shifts: computed(() => visiblePvsShifts(items())),
  })),
  withComputed((store) => ({
    actionableCount: computed(() => countActionablePvsShifts(store.shifts())),
  })),
  withMethods(
    (
      store,
      api = inject(PvsShiftApiService),
      toast = inject(ToastService),
      translate = inject(TranslateService),
    ) => {
      const notifyError = (summaryKey: string, error: IHttpError): void => {
        toast.add({
          level: 'error',
          summary: translate.instant(summaryKey),
          detail: parseError(error),
        });
      };

      const saveList = (items: IPvsShiftForemanInfo[]): void => {
        patchState(store, {
          items,
          status: EStateStatus.SUCCESS,
          error: null,
        });
      };

      const reloadAfter = (
        request: Observable<unknown>,
        successKey: string,
        errorKey: string,
      ): Observable<unknown> =>
        request.pipe(
          tapResponse({
            next: () => {
              toast.add({
                level: 'success',
                summary: translate.instant(successKey),
              });
            },
            error: (error: IHttpError) => {
              patchState(store, { status: EStateStatus.ERROR, error });
              notifyError(errorKey, error);
            },
          }),
          switchMap(() =>
            api.getForemanList().pipe(
              tapResponse({
                next: (items) => saveList(items),
                error: (error: IHttpError) => {
                  patchState(store, { status: EStateStatus.ERROR, error });
                  notifyError('PVS_SHIFT.FUNCTIONS.LOAD.ERROR', error);
                },
              }),
            ),
          ),
        );

      const handleLoadError = (error: IHttpError, silent: boolean): void => {
        if (silent && store.items().length > 0) {
          patchState(store, { error });
          return;
        }

        patchState(store, { status: EStateStatus.ERROR, error });
        if (!silent) {
          notifyError('PVS_SHIFT.FUNCTIONS.LOAD.ERROR', error);
        }
      };

      const load = rxMethod<PvsShiftLoadOptions>(
        pipe(
          tap((options) => {
            if (!options.silent) {
              patchState(store, {
                status: EStateStatus.LOADING,
                error: null,
              });
            }
          }),
          switchMap((options) =>
            api.getForemanList().pipe(
              tapResponse({
                next: (items) => saveList(items),
                error: (error: IHttpError) =>
                  handleLoadError(error, options.silent === true),
              }),
            ),
          ),
        ),
      );

      return {
        load,
        watchAccess: rxMethod<boolean>(
          pipe(
            distinctUntilChanged(),
            switchMap((canRefresh) => {
              if (!canRefresh) {
                patchState(store, initialState);
                return EMPTY;
              }

              return timer(0, PVS_SHIFT_REFRESH_INTERVAL_MS).pipe(
                tap((tick) => load(tick === 0 ? {} : { silent: true })),
              );
            }),
          ),
        ),
        start: rxMethod<number>(
          pipe(
            tap(() =>
              patchState(store, {
                status: EStateStatus.LOADING,
                error: null,
              }),
            ),
            exhaustMap((id) =>
              reloadAfter(
                api.start(id),
                'PVS_SHIFT.FUNCTIONS.START.SUCCESS',
                'PVS_SHIFT.FUNCTIONS.START.ERROR',
              ),
            ),
          ),
        ),
        finish: rxMethod<number>(
          pipe(
            tap(() =>
              patchState(store, {
                status: EStateStatus.LOADING,
                error: null,
              }),
            ),
            exhaustMap((id) =>
              reloadAfter(
                api.finish(id),
                'PVS_SHIFT.FUNCTIONS.FINISH.SUCCESS',
                'PVS_SHIFT.FUNCTIONS.FINISH.ERROR',
              ),
            ),
          ),
        ),
      };
    },
  ),
  withHooks({
    onInit(store) {
      const access = inject(AccessService);

      store.watchAccess(access.selectAccess(EAccessCode.s_ActivePvsTask));
    },
  }),
);

export function visiblePvsShifts(
  shifts: readonly IPvsShiftForemanInfo[],
): IPvsShiftForemanInfo[] {
  return shifts
    .filter(
      (shift) => shift.stateCode !== EPvsShiftStatusCode.PVS_SHIFT_COMPLETED,
    )
    .slice()
    .sort(
      (left, right) => Date.parse(left.startDuty) - Date.parse(right.startDuty),
    );
}

export function countActionablePvsShifts(
  shifts: readonly IPvsShiftForemanInfo[],
): number {
  return shifts.filter((shift) => shift.isCanStart || shift.isCanFinish).length;
}
